/*
 * Copyright (C) 2020 Reciprocity, Inc - All Rights Reserved
 * Unauthorized use, copying, distribution, displaying, or public performance
 * of this file, via any medium, is strictly prohibited. All information
 * contained herein is proprietary and confidential and may not be shared
 * with any third party without the express written consent of Reciprocity, Inc.
 */

import gql from 'graphql-tag';
import { isObject, reduce, split, startsWith } from 'lodash';

import { getAttributeFragment, getCustomAttributeFragment, getMappedAttributeFragment } from './AttributesQueryConfig';

export default (objectType: string, columnsMeta: object, isCommentable: boolean, columns: Array<string | object>) => {
  const regularColumns: string[] = columns.filter(
    (column: string | object) => !isObject(column) && !startsWith(column, 'mappings.')
  ) as string[];
  const customAttributeColumns: object[] = columns.filter(
    (column: string | object) =>
      isObject(column) && (startsWith(column.name, 'ca__') || startsWith(column.name, 'dynamic__'))
  ) as object[];
  const mappedColumns: string[] = columns.filter(
    (column: string | object) => !isObject(column) && startsWith(column, 'mappings.')
  ) as string[];

  const isSurveyResponse: boolean = objectType == 'SurveyResponse';
  /**
   * Loops over queried columns and constructs regular attributes query
   * Example:
   * `
   *   id // Does not need sub-selection
   *   title {
   *     value
   *   }
   * `
   */
  const regularColumnsWithSubSelections = reduce(
    regularColumns,
    (s: string, column) => {
      if (getAttributeFragment(objectType, column)) s += `${getAttributeFragment(objectType, column)}\n`;
      return s;
    },
    ''
  );

  /**
   * Loops over queried mapped objects columns and constructs custom attributes query
   * Example:
   * `
   *   ca__1: customAttribute(name: ca__1) {
   *     name
   *     ... on CustomAttributeDate {
   *       value
   *     }
   *   }
   * `
   */
  // TODO: rename other two queries
  const customAttributesColumnsQuery = reduce(
    customAttributeColumns,
    (s: string, caColumn: object) => {
      s += `${getCustomAttributeFragment(caColumn.name, caColumn.type)}\n`;
      return s;
    },
    ''
  );

  /**
   * Loops over queried mapped objects columns and constructs mappings query
   * Example:
   * `
   *   mappings {
   *     programs {
   *       edges {
   *         node {
   *           id
   *           sorCode { value }
   *           sorTitle { value }
   *         }
   *       }
   *     }
   *   }
   * `
   */
  const mappedColumnsWithSubSelections = reduce(
    mappedColumns,
    (s: string, column: string) => {
      /**
       * Takes the object type from mappings column key,
       * i.e.: 'program' from 'mappings.program'
       */
      const objectType: string = split(column, '.')[1];

      s += `${getMappedAttributeFragment(objectType)}\n`;
      return s;
    },
    ''
  );
  const mappedColumnsFragment = mappedColumnsWithSubSelections
    ? `
      mappings {
        ${mappedColumnsWithSubSelections}
      }
    `
    : ``;

  const slug = columnsMeta.attrs.some(el => el.attrName === 'slug')
    ? `
    slug{
      value
    }
  `
    : '';

  const questionnairePublicId = `
    publicId{
      value
    }
    status {
      value
    }
    objectType {
      value
    }
    objectId {
      value
    }
  `;

  const isOverdueFragment = ['Assessment', 'Request', 'Task'].some(el => el == objectType)
    ? `
      isOverdue {
        value
      }
    `
    : ``;

  const nodePermissions = `
      permissions {
        canDelete
        canMap
        canAttach
        canCreateTask
        canSendSurveys
        canViewSurveys
        surveyEmailTemplates {
          canClone
          canPreview
        }
        surveyResponses {
          canResend
          canClose
          canDownloadAttachments
          canExport
        }
        surveyTemplates {
          canDuplicate
          canPreview
          canExport
        }
      }
    `;
  return gql`
    query SorListingObjects($objectType: SearchType!, $pageSize: Int!, $currPage: Int!, $order: OrderBy!, $filter: JSONString!) {
      search(type: $objectType, first: $pageSize, page: $currPage, order: $order, filter: $filter) {
        totalCount
        edges {
          node {
            ... on ${objectType} {
              id
              __typename
              dbId
              ${slug}
              ${isCommentable ? 'hasUnreadComments' : ''}
              ${isSurveyResponse ? questionnairePublicId : ''}


              ${regularColumnsWithSubSelections}
              ${customAttributesColumnsQuery}
              ${mappedColumnsFragment}
              ${nodePermissions}
              ${isOverdueFragment}
            }
          }
        }
      }
    }
  `;
};
